import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  setDoc
} from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';

const UPLOAD_WORKER_URL = 'https://divine-dew-5546.iwebdesigner-az.workers.dev';

// ============ Auth ============
export const loginAdmin = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

export const logoutAdmin = async () => {
  await signOut(auth);
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// ============ Upload to Cloudflare R2 via Worker ============
export const uploadImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.split(',')[1];
        const response = await fetch(UPLOAD_WORKER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
            fileData: base64,
          }),
        });
        
        const data = await response.json();
        
        if (data.success && data.url) {
          resolve(data.url);
        } else {
          reject(new Error(data.error || 'Upload failed'));
        }
      } catch (error) {
        console.error('Upload error:', error);
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('File reading failed'));
    reader.readAsDataURL(file);
  });
};

// ============ Categories ============
export const getCategories = async () => {
  try {
    const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch {
    const snapshot = await getDocs(collection(db, 'categories'));
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return docs.sort((a, b) => (a.order || 0) - (b.order || 0));
  }
};

export const getCategory = async (id) => {
  const docRef = doc(db, 'categories', id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const addCategory = async (data) => {
  const docRef = await addDoc(collection(db, 'categories'), {
    ...data,
    created_at: new Date().toISOString()
  });
  return { id: docRef.id, ...data };
};

export const updateCategory = async (id, data) => {
  const docRef = doc(db, 'categories', id);
  await updateDoc(docRef, data);
  return { id, ...data };
};

export const deleteCategory = async (id) => {
  await deleteDoc(doc(db, 'categories', id));
};

// ============ Products ============
export const getProducts = async (categoryId = null) => {
  try {
    let q;
    if (categoryId) {
      q = query(collection(db, 'products'), where('category_id', '==', categoryId));
    } else {
      q = query(collection(db, 'products'));
    }
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return docs.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch {
    const snapshot = await getDocs(collection(db, 'products'));
    let docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (categoryId) docs = docs.filter(d => d.category_id === categoryId);
    return docs.sort((a, b) => (a.order || 0) - (b.order || 0));
  }
};

export const getProduct = async (id) => {
  const docRef = doc(db, 'products', id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

export const addProduct = async (data) => {
  const docRef = await addDoc(collection(db, 'products'), {
    ...data,
    created_at: new Date().toISOString()
  });
  return { id: docRef.id, ...data };
};

export const updateProduct = async (id, data) => {
  const docRef = doc(db, 'products', id);
  await updateDoc(docRef, data);
  return { id, ...data };
};

export const deleteProduct = async (id) => {
  await deleteDoc(doc(db, 'products', id));
};

// ============ Banners ============
export const getBanners = async (activeOnly = false) => {
  try {
    const snapshot = await getDocs(collection(db, 'banners'));
    let docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    if (activeOnly) docs = docs.filter(d => d.is_active === true);
    return docs.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch {
    return [];
  }
};

export const addBanner = async (data) => {
  const docRef = await addDoc(collection(db, 'banners'), {
    ...data,
    created_at: new Date().toISOString()
  });
  return { id: docRef.id, ...data };
};

export const updateBanner = async (id, data) => {
  const docRef = doc(db, 'banners', id);
  await updateDoc(docRef, data);
  return { id, ...data };
};

export const deleteBanner = async (id) => {
  await deleteDoc(doc(db, 'banners', id));
};

// ============ Pages ============
export const getPageContent = async (pageName) => {
  const docRef = doc(db, 'pages', pageName);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return { id: pageName, page_name: pageName, content: {} };
};

export const updatePageContent = async (pageName, content) => {
  const docRef = doc(db, 'pages', pageName);
  await setDoc(docRef, {
    page_name: pageName,
    content,
    updated_at: new Date().toISOString()
  }, { merge: true });
};

// ============ Contact Info ============
export const getContactInfo = async () => {
  const docRef = doc(db, 'settings', 'contact_info');
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return {
    id: 'contact_info',
    phone: '+994 50 655 55 01',
    email: 'info@polymax.az',
    address: 'Bakı şəhəri, Azərbaycan',
    whatsapp: '+994506555501',
    working_hours: 'Bazar ertəsi - Cümə: 09:00 - 18:00'
  };
};

export const updateContactInfo = async (data) => {
  const docRef = doc(db, 'settings', 'contact_info');
  await setDoc(docRef, {
    ...data,
    updated_at: new Date().toISOString()
  }, { merge: true });
};

// ============ Contact Messages ============
export const getContactMessages = async () => {
  try {
    const q = query(collection(db, 'contact_messages'), orderBy('created_at', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch {
    const snapshot = await getDocs(collection(db, 'contact_messages'));
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return docs.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }
};

export const addContactMessage = async (data) => {
  const docRef = await addDoc(collection(db, 'contact_messages'), {
    ...data,
    is_read: false,
    created_at: new Date().toISOString()
  });
  return { id: docRef.id, ...data };
};

export const markMessageRead = async (id) => {
  const docRef = doc(db, 'contact_messages', id);
  await updateDoc(docRef, { is_read: true });
};

export const deleteContactMessage = async (id) => {
  await deleteDoc(doc(db, 'contact_messages', id));
};
